/**
 * The PricingBox component is a React component that displays pricing information and allows users to
 * select different plans.
 * @param {any} props - The `props` parameter is an object that contains the following properties:
 * @returns The PricingBox component is returning a JSX element.
 */
import { CheckIcon, InformationCircleIcon } from '@heroicons/react/24/solid';
import { useState } from 'react';
import AWSMarketplace from '../../../../images/aws-mkt.png';

export default function PricingBox(props: any) {
  const [environmentCount, setEnvironmentCount] = useState(props.account?.quantityPurchased ? props.account.quantityPurchased : 1);
  const products = props.products.filter((product: any) => product.recurring.interval === 'month').sort((a: any, b: any) => a.product[0].name.localeCompare(b.name));
  const productType = props?.account ? props?.account?.label?.name?.substring(0,3) : '';

  const activePlan = 'mx-6 border border-cyan-400 rounded shadow-sm divide-y divide-cyan-400 bg-gray-700 animate-color-change-2x ring-2 ring-cyan-500 hover:ring-pink-400 cursor-pointer';
  const inactivePlan = 'mx-6 border border-gray-400 rounded shadow-sm divide-y divide-cyan-400 bg-gray-700 hover:animate-color-change-2x ring-2 ring-cyan-500 hover:ring-pink-400 cursor-pointer';
  
  const prodPrice = 100000;

  const incrementEnvironment = () => setEnvironmentCount(environmentCount+1);
  const decrementEnvironment = () => setEnvironmentCount(() => Math.max(1, environmentCount - 1));
 
  return (
    <div className="pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="sm:flex sm:flex-col sm:align-center">
          <p className="my-5 text-4xl font-bold text-gray-200 sm:text-center">
            Start building for free, upgrade when you're ready.
          </p>
        </div>
        <div className="mt-2 space-y-4 sm:mt-2 sm:space-y-0 sm:grid sm:grid-cols-2 sm:gap-2 lg:max-w-7xl lg:mx-auto xl:max-w-none xl:mx-0 xl:grid-cols-2">
          { props.account && products.map((product: any) => (
            <div key={product.product[0].name} className={ productType == product.product[0].name.substring(0,3) ? activePlan : inactivePlan }>
              <div className='p-6 bg-gray-700 border rounded-t'>
                <h2 className="text-2xl leading-6 text-center font-medium text-pink-400">
                  {product.product[0].name}
                </h2>         
              </div>  
              <div className="px-14 pt-2 w-full items-center pb-4">  
              { props.account.type == 'aws' && 
              <div className='inline px-6'>
                &nbsp;
              </div> 
              } 
                { product.product[0].name != 'Developer Plan' &&
                  <p className="mt-2 pl-4 text-center inline">
                    <span className="text-4xl font-extrabold text-center align-baseline text-cyan-400">
                      ${`${prodPrice / 100 * (environmentCount)}`}
                    </span>
                    <span className="text-md font-medium text-cyan-400">/{product.recurring.interval}</span>
                  </p>
                }
                { product.product[0].name != 'Developer Plan' && 
                  <div className="inline text-xs font-medium environment-controls justify-center items-center mt-2">
                    <span className={`${environmentCount !== props.account.quantityPurchased ? 'text-pink-500' : 'text-gray-200'}`}>
                      /environment (x{environmentCount})
                    </span>
                  { props.account.type == 'stripe' &&
                    <span className="border ml-2 p-2 rounded border-cyan-400">
                      <button onClick={decrementEnvironment} className="text-gray-200 text-lg px-2">-</button>
                      <button onClick={incrementEnvironment} className="text-cyan-400 text-lg px-2">+</button>
                    </span>
                  }
                  </div> 
                }
                { product.product[0].name == 'Developer Plan' && 
                  <a className="mt-5 mb-5 block w-full py-2 text-4xl font-semibold text-gray-200 text-center">Free Account</a>
                }
                { product.product[0].name != 'Developer Plan' && (props.account.customerIdentifier && props.account.type == 'stripe') &&
                  <a 
                    href='#' onClick={() => props.account.customerIdentifier ? props.getCustomerPortal(props.account.customerIdentifier) : props.selectProduct(product)} 
                    className="mt-4 block w-full bg-gray-600 border border-cyan-500 rounded py-2 text-sm font-semibold text-white text-center hover:bg-cyan-400"
                  >
                    Manage
                  </a>
                }
                { product.product[0].name != 'Developer Plan' && props.account.type == 'aws' &&
                <a 
                  href={props.awsProductURL} 
                  target='_blank' rel="noopener noreferrer" 
                  className="mt-4 block w-full bg-gray-600 border border-gray-500 rounded py-2 text-sm font-semibold text-white text-center hover:bg-pink-400">
                  Manage with
                  <img src={AWSMarketplace} alt="AWS Marketplace" className="inline h-12 py-2 ml-2" />
                </a>
                }
              </div>
              <div className="py-4 px-6">
                <h3 className="text-xs font-medium text-cyan-600 tracking-wide uppercase">What's included</h3>
                <ul role="list" className="mt-6 space-y-4">
                  {product.features.map((feature: any) => (
                    <li key={feature} className="flex space-x-3">
                      <CheckIcon className="flex-shrink-0 h-5 w-5 text-cyan-400" aria-hidden="true" />
                      <span className="text-sm text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
          {!props.account && products.sort((a: any, b: any) => a.product[0].name.localeCompare(b.name)).map((product: any) => (
            <div key={product.product[0].name} className={inactivePlan}>
              <div className='p-6 bg-gray-700 rounded-t hover:rounded-t'>
                <h2 className="text-2xl leading-6 text-center font-medium text-red-400">{product.product[0].name}</h2>         
              </div>       
              <div className="px-6 pb-6">    
                {product.product[0].name != 'Developer Plan' ? <>
                <p className="mt-8 text-center">
                  <span className="text-4xl font-extrabold text-center text-cyan-400">
                     ${`${prodPrice / 100}`}
                  </span>{' '}
                    <span className="text-base font-medium text-gray-500">/{product.recurring.interval}</span> 
                    
                  </p>
                  </>
                : 
                <a 
                  onClick={(e)=> { e.preventDefault(); props.setRegisterModal(true)} } 
                  className="cursor-pointer pt-6 block w-full py-2 text-4xl font-semibold text-gray-200 text-center hover:animate-pulse hover:text-pink-300"
                >
                  Free
                </a>
                }
              </div>
              <div className="pt-2 pb-8 px-6">
                { product.product[0].name != 'Developer Plan' ?
                  <a href="https://docs.retrohook.com/docs/costs" target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-gray-200 hover:animate-pulse hover:text-rose-300">
                    <InformationCircleIcon className="h-8 w-8 pb-1 inline align-middle text-cyan-300 rounded cursor-pointer hover:text-cyan-500" />
                  Cost of Ownership Calculator
                  </a>
                  : ''
                }
                {product.product[0].name == 'Developer Plan' ?
                  <h3 className="text-sm mt-8 font-medium text-cyan-600 tracking-wide uppercase">What's included</h3>
                  :
                  <h3 className="text-sm font-medium text-cyan-600 tracking-wide uppercase">What's included</h3>
                }
                <ul role="list" className="mt-6 space-y-4">
                  {product.features.map((feature: any) => (
                    <li key={feature} className="flex space-x-3">
                      <CheckIcon className="flex-shrink-0 h-5 w-5 text-cyan-400" aria-hidden="true" />
                      <span className="text-sm text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
